import { AUTH_PROVIDER_COOKIE } from '@/lib/auth-constants';
import { AUTH0_SESSION_COOKIE } from '@/lib/auth0/server-cookies';
import { AuthProvider } from '@heidi/auth/contracts';
import type { IncomingMessage } from 'http';

/** Subset of `_document` getInitialProps context used by the GTM route gate. */
export type GtmDocumentContext = {
  pathname?: string;
  asPath?: string;
  req?: IncomingMessage;
};

/** Pre-signup funnel routes where the GTM container may load (GRO-3977). */
const GTM_ALLOWED_PREFIXES = ['/register', '/login', '/onboarding', '/chat'] as const;

const NEXTAUTH_SESSION_COOKIE = 'next-auth.session-token';
const NEXTAUTH_SECURE_SESSION_COOKIE = '__Secure-next-auth.session-token';
const KINDE_SESSION_COOKIE_RE = /^(access_token|id_token|refresh_token)\d*$/;
export const GTM_CONTAINER_ID = 'GTM-5VSCMP5N';

function isGtmAllowedRoute(pathname: string): boolean {
  if (pathname === '/') {
    return true;
  }

  return GTM_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function isAuthSessionCookieName(name: string): boolean {
  if (name === AUTH0_SESSION_COOKIE || name.startsWith(`${AUTH0_SESSION_COOKIE}__`)) {
    return true;
  }

  if (name === NEXTAUTH_SESSION_COOKIE || name === NEXTAUTH_SECURE_SESSION_COOKIE) {
    return true;
  }

  return KINDE_SESSION_COOKIE_RE.test(name);
}

/** Cookie-only session probe — no auth SDK roundtrip in _document. */
function hasAuthSessionCookie(req: IncomingMessage | undefined): boolean {
  const cookie = req?.headers?.cookie ?? '';
  if (!cookie) {
    return false;
  }

  const providers = Object.values(AuthProvider) as string[];
  for (const part of cookie.split(';')) {
    const trimmed = part.trim();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx <= 0) {
      continue;
    }
    const name = trimmed.slice(0, eqIdx);
    const value = trimmed.slice(eqIdx + 1);
    if (name === AUTH_PROVIDER_COOKIE && providers.includes(value)) {
      return true;
    }
    if (isAuthSessionCookieName(name)) {
      return true;
    }
  }

  return false;
}

function buildClientSessionCookieGuard(): string {
  return (
    `n==='${AUTH0_SESSION_COOKIE}'||n.indexOf('${AUTH0_SESSION_COOKIE}__')===0` +
    `||n==='${NEXTAUTH_SESSION_COOKIE}'||n==='${NEXTAUTH_SECURE_SESSION_COOKIE}'` +
    `||/^(access_token|id_token|refresh_token)\\d*$/.test(n)`
  );
}

/**
 * Whether to inject the GTM container on this document request.
 *
 * Route allowlist is the primary gate (works on static prerender / export).
 * When `ctx.req` exists (SSR), suppress GTM for authenticated sessions.
 * Statically prerendered funnel HTML also guards at runtime via
 * `buildGtmBootstrapScript`.
 */
export function shouldLoadGtm(ctx: GtmDocumentContext): boolean {
  const raw = ctx.asPath ?? ctx.pathname ?? '/';
  const pathname = raw.split('?')[0]?.split('#')[0] || '/';
  if (!isGtmAllowedRoute(pathname)) {
    return false;
  }

  // Static prerender / static export: no request — route gate only; runtime
  // cookie guard in buildGtmBootstrapScript closes the authenticated-user gap.
  if (!ctx.req) {
    return true;
  }

  return !hasAuthSessionCookie(ctx.req);
}

/**
 * GTM bootstrap IIFE with a synchronous document.cookie guard for statically
 * prerendered funnel pages (ctx.req absent at build time).
 *
 * Session token cookies are HttpOnly and invisible here — the client-readable
 * `heidi-auth-provider` discriminator is the reliable runtime signal.
 */
export function buildGtmBootstrapScript(): string {
  const providerValuesPattern = Object.values(AuthProvider).join('|');
  const sessionCookieGuard = buildClientSessionCookieGuard();

  return `(function(w,d,s,l,i){
var c=d.cookie?d.cookie.split(';'):[],n,j,v;
for(j=0;j<c.length;j++){n=c[j].trim();var e=n.indexOf('=');if(e<=0)continue;v=n.slice(e+1);n=n.slice(0,e);
if(n==='${AUTH_PROVIDER_COOKIE}'&&/^(?:${providerValuesPattern})$/.test(v))return;
if(${sessionCookieGuard})return;}
w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;
}
